import { Container, ObservablePoint, Rectangle, Point, interaction } from "pixi.js";
import merge from "lodash/merge";
import { Spring, PartialSpringConfig } from "wobble";
import { distance2d, clamp } from "@cc/maths-js";
import { DraggableEvents } from "./DraggableEvents";
import { DraggableOptions } from "./DraggableOptions";

/**
 */
export class Draggable extends Container {
  private static currentTouchIdentifier = -1;

  /**
   */
  public targetPositions: ObservablePoint[] = [];
  public movementBounds: Rectangle;
  public restPosition: PIXI.PointLike;
  public targetRadius: number = 128;
  public targetRadiusAuto?: number;
  public snapToCenter: boolean = false;
  public springConfig: PartialSpringConfig = {
    stiffness: 1000,
    damping: 100,
    mass: 1
  };

  /**
   */
  private pointerDown: boolean = false;
  private pointerOffset: Point;
  private springX: Spring;
  private springY: Spring;
  private springScale: Spring;
  private springAlpha: Spring;
  private springRotation: Spring;
  private touchIdentifier: number;

  /**
   */
  constructor(options: DraggableOptions) {
    super();
    merge(this, options);
    this.pointerDown = false;
    this.pointerOffset = new Point();
    this.addInteractionEvents();
    this.createSprings();
  }

  /**
   */
  private createSprings() {
    this.springX = new Spring(this.springConfig);
    this.springX.updateConfig({ fromValue: 0 });
    this.springX.onUpdate(s => {
      this.x = s.currentValue;
    });

    this.springY = new Spring(this.springConfig);
    this.springY.updateConfig({ fromValue: 0 });
    this.springY.onUpdate(s => {
      this.y = s.currentValue;
    });

    this.springRotation = new Spring(this.springConfig);
    this.springRotation.updateConfig({ fromValue: 0 });
    this.springRotation.onUpdate(s => {
      this.rotation = s.currentValue;
    });

    this.springAlpha = new Spring(this.springConfig);
    this.springAlpha.updateConfig({ fromValue: 0 });
    this.springAlpha.onUpdate(s => {
      this.alpha = s.currentValue;
    });

    this.springScale = new Spring(this.springConfig);
    this.springScale.updateConfig({ fromValue: 0 });
    this.springScale.onUpdate(s => {
      this.scale.set(s.currentValue);
    });
  }

  /**
   */
  destroy(options: PIXI.DestroyOptions) {
    if (Draggable.currentTouchIdentifier === this.touchIdentifier)
      Draggable.currentTouchIdentifier = -1;
    this.destroySpring(this.springX);
    this.destroySpring(this.springY);
    this.destroySpring(this.springAlpha);
    this.destroySpring(this.springRotation);
    this.destroySpring(this.springScale);
    super.destroy(options);
  }

  /**
   * @param spring
   */
  destroySpring(spring: Spring) {
    spring.stop();
    spring.removeAllListeners();
  }

  /**
   */
  addInteractionEvents() {
    this.interactive = true;
    this.on("pointerdown", this.onPointerDown);
    this.on("pointerup", this.onPointerUp);
    this.on("pointerupoutside", this.onPointerUp);
    this.on("pointercancel", this.onPointerUp);
    this.on("pointermove", this.onPointerMove);
  }

  /**
   */
  onPointerMove(e: interaction.InteractionEvent) {
    if (this.pointerDown && e.data.identifier === Draggable.currentTouchIdentifier) {
      const localPosition = e.data.getLocalPosition(this.parent);
      this.setPosition(
        localPosition.x - this.pointerOffset.x,
        localPosition.y - this.pointerOffset.y
      );
      this.emit(DraggableEvents.DragMove, this, e);
      if (typeof this.targetRadiusAuto === "number") {
        const hitTarget = this.checkTargetPosition(this.targetRadiusAuto);
        if (hitTarget) {
          this.dragEndOnTarget(e, hitTarget);
          Draggable.currentTouchIdentifier = -1;
          this.pointerDown = false;
        }
      }
    }
  }

  /**
   */
  onPointerDown(e: interaction.InteractionEvent) {
    if (Draggable.currentTouchIdentifier === -1) {
      Draggable.currentTouchIdentifier = e.data.identifier;
      this.touchIdentifier = Draggable.currentTouchIdentifier;
      const localPosition = e.data.getLocalPosition(this.parent);
      this.pointerOffset.x = this.snapToCenter ? 0 : localPosition.x - this.x;
      this.pointerOffset.y = this.snapToCenter ? 0 : localPosition.y - this.y;
      this.pointerDown = true;
      this.emit(DraggableEvents.DragStart, this, e);
      this.onPointerMove(e);
    }
  }

  /**
   */
  onPointerUp(e: interaction.InteractionEvent) {
    if (Draggable.currentTouchIdentifier === e.data.identifier) {
      this.pointerDown = false;
      Draggable.currentTouchIdentifier = -1;
      const hitTarget = this.checkTargetPosition();
      if (hitTarget) this.dragEndOnTarget(e, hitTarget);
      else this.dragEnd(e);
    }
  }

  /**
   */
  dragEndOnTarget(e: interaction.InteractionEvent, hitTarget: PIXI.PointLike) {
    this.emit(DraggableEvents.DragEndOnTarget, this, e);
    this.setPosition(hitTarget.x, hitTarget.y);
    this.springX.onStop(this.dragEndOnTargetStop.bind(this));
    this.interactive = false;
  }

  /**
   */
  dragEndOnTargetStop() {
    this.emit(DraggableEvents.DragEndOnTargetStop, this);
    this.springX.removeListener(this.dragEndOnTargetStop.bind(this));
  }

  /**
   */
  dragEnd(e: interaction.InteractionEvent) {
    this.emit(DraggableEvents.DragEnd, this, e);
    if (this.restPosition) {
      this.setPosition(this.restPosition.x, this.restPosition.y);
    }
  }

  /**
   * @param {number} x
   * @param {number} y
   */
  setPosition(x: number, y: number, dontAnimate?: boolean) {
    if (typeof this.movementBounds !== "undefined") {
      x = clamp(x, this.movementBounds.x, this.movementBounds.x + this.movementBounds.width);
      y = clamp(y, this.movementBounds.y, this.movementBounds.y + this.movementBounds.height);
    }
    if (dontAnimate) {
      this.springX.updateConfig({ fromValue: x, toValue: x });
      this.springY.updateConfig({ fromValue: y, toValue: y });
      this.position.set(x, y);
    } else {
      this.springX.updateConfig({ toValue: x });
      this.springY.updateConfig({ toValue: y });
      this.springX.start();
      this.springY.start();
    }
  }

  /**
   * @param {number} value
   */
  setScale(value: number, dontAnimate?: boolean) {
    if (dontAnimate) {
      this.springScale.updateConfig({ fromValue: value });
      this.scale.set(value);
    } else {
      this.springScale.updateConfig({ toValue: value });
      this.springScale.start();
    }
  }

  /**
   * @param {number} value
   */
  setAlpha(value: number, dontAnimate?: boolean) {
    if (dontAnimate) {
      this.springAlpha.updateConfig({ fromValue: value });
      this.alpha = value;
    } else {
      this.springAlpha.updateConfig({ fromValue: this.alpha, toValue: value });
      this.springAlpha.start();
    }
  }

  /**
   * @param {number} value
   */
  setRotation(value: number, dontAnimate?: boolean) {
    if (dontAnimate) {
      this.springRotation.updateConfig({ fromValue: value });
      this.rotation = value;
    } else {
      this.springRotation.updateConfig({
        fromValue: this.rotation,
        toValue: value
      });
      this.springRotation.start();
    }
  }

  /**
   *
   */
  checkTargetPosition(radius?: number) {
    radius = radius || this.targetRadius;
    let collide: ObservablePoint | null = null;
    for (let i = 0; i < this.targetPositions.length; i++) {
      const targetPosition = this.targetPositions[i];
      if (distance2d(this.position, targetPosition, true) < radius * radius)
        collide = targetPosition;
    }
    return collide;
  }
}
